import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loadMapsLibraries } from "@/lib/googleMaps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MapTrifold,
  Plus,
  PencilSimple,
  Trash,
  Polygon,
  Truck,
  Package as PackageIcon,
  Buildings,
  X,
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";

const DEFAULT_CENTER = { lat: 18.0179, lng: -76.8099 }; // Kingston, Jamaica
const PALETTE = ["#002BF6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9", "#EC4899", "#14B8A6"];

const EMPTY_FORM = {
  id: null,
  name: "",
  color: PALETTE[0],
  polygon: [],
  assigned_driver_ids: [],
  assigned_branch_id: null,
  notes: "",
  is_active: true,
};

export default function Routes() {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const editorPolygonRef = useRef(null); // committed polygon being edited
  const drawingStateRef = useRef({ vertices: [], markers: [], polyline: null, clickListener: null });
  const overviewPolygonsRef = useRef({}); // id -> google.maps.Polygon for the overview view
  const googleRef = useRef(null);

  const canManage = ["super_admin", "company_admin"].includes(user.role);

  // Initial load
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [r, d, b] = await Promise.all([
        api.get("/routes"),
        api.get("/drivers", { params: { include_zipx: true } }).catch(() => ({ data: [] })),
        api.get("/branches").catch(() => ({ data: [] })),
      ]);
      setRoutes(r.data || []);
      setDrivers(Array.isArray(d.data) ? d.data : []);
      setBranches(Array.isArray(b.data) ? b.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load routes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Init Google Map (overview)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const g = await loadMapsLibraries(["maps", "geometry"]);
        if (cancelled || !mapDivRef.current) return;
        googleRef.current = g;
        if (!mapRef.current) {
          mapRef.current = new g.maps.Map(mapDivRef.current, {
            center: DEFAULT_CENTER,
            zoom: 11,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          });
        }
      } catch (e) {
        toast.error("Failed to load Google Maps — check the API key");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Render overview polygons whenever routes list changes
  useEffect(() => {
    const g = googleRef.current;
    const map = mapRef.current;
    if (!g || !map) return;

    // Remove old
    Object.values(overviewPolygonsRef.current).forEach((p) => p.setMap(null));
    overviewPolygonsRef.current = {};
    if (open) return; // hide overview while editor dialog is open (separate map)

    const bounds = new g.maps.LatLngBounds();
    routes.forEach((r) => {
      if (!r.polygon || r.polygon.length < 3) return;
      const path = r.polygon.map(([lat, lng]) => ({ lat, lng }));
      const poly = new g.maps.Polygon({
        paths: path,
        strokeColor: r.color || "#002BF6",
        strokeOpacity: r.is_active ? 0.9 : 0.4,
        strokeWeight: selectedId === r.id ? 3 : 2,
        fillColor: r.color || "#002BF6",
        fillOpacity: r.is_active ? 0.18 : 0.07,
        map,
      });
      poly.addListener("click", () => setSelectedId(r.id));
      path.forEach((pt) => bounds.extend(pt));
      overviewPolygonsRef.current[r.id] = poly;
    });
    if (!bounds.isEmpty() && routes.length > 0) {
      map.fitBounds(bounds, 40);
    }
  }, [routes, selectedId, open]);

  // -------- Editor dialog map: manual click-to-draw + edit (DrawingManager deprecated in v3.65) --------
  const editorMapDivRef = useRef(null);
  const editorMapRef = useRef(null);

  const cleanupDrawingState = () => {
    const g = googleRef.current;
    const state = drawingStateRef.current;
    if (state.clickListener && g) g.maps.event.removeListener(state.clickListener);
    state.markers.forEach((m) => m.setMap(null));
    if (state.polyline) state.polyline.setMap(null);
    drawingStateRef.current = { vertices: [], markers: [], polyline: null, clickListener: null };
  };

  const startDrawingMode = () => {
    const g = googleRef.current;
    const map = editorMapRef.current;
    if (!g || !map) return;
    cleanupDrawingState();
    const state = drawingStateRef.current;
    // Polyline traces the in-progress edge
    state.polyline = new g.maps.Polyline({
      path: [],
      strokeColor: form.color,
      strokeOpacity: 0.9,
      strokeWeight: 2,
      map,
    });

    const closePolygon = () => {
      const path = state.vertices.map(({ lat, lng }) => ({ lat, lng }));
      if (path.length < 3) return;
      // Tear down drawing artefacts
      cleanupDrawingState();
      // Commit editable polygon
      const poly = new g.maps.Polygon({
        paths: path,
        strokeColor: form.color,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: form.color,
        fillOpacity: 0.2,
        editable: true,
        draggable: true,
        map,
      });
      editorPolygonRef.current = poly;
      attachPolygonChangeListeners(poly);
      syncFormFromPolygon(poly);
    };

    state.clickListener = map.addListener("click", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      // Close-on-tap-near-first-vertex (≥3 vertices required)
      if (state.vertices.length >= 3) {
        const first = state.vertices[0];
        const dLat = Math.abs(first.lat - lat);
        const dLng = Math.abs(first.lng - lng);
        // ~150m threshold at city zoom — close enough for "tap the first dot to finish"
        if (dLat < 0.0015 && dLng < 0.0015) {
          closePolygon();
          return;
        }
      }
      state.vertices.push({ lat, lng });
      // Marker per vertex
      const marker = new g.maps.Marker({
        position: { lat, lng },
        map,
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          scale: state.vertices.length === 1 ? 7 : 5,
          fillColor: state.vertices.length === 1 ? "#10B981" : form.color,
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
      });
      // First marker closes the polygon when clicked
      if (state.vertices.length === 1) {
        marker.addListener("click", () => {
          if (state.vertices.length >= 3) closePolygon();
        });
      }
      state.markers.push(marker);
      // Update polyline
      if (state.polyline) {
        state.polyline.setPath(state.vertices.map(({ lat: la, lng: ln }) => ({ lat: la, lng: ln })));
      }
      // Bubble vertex count to form so footer "Vertices: N" reflects live
      setForm((f) => ({ ...f, polygon: state.vertices.map((v) => [v.lat, v.lng]) }));
    });
  };

  useEffect(() => {
    if (!open) return;
    const g = googleRef.current;
    if (!g) return;

    // Defer until DOM element exists
    const timer = setTimeout(() => {
      if (!editorMapDivRef.current) return;
      if (!editorMapRef.current) {
        editorMapRef.current = new g.maps.Map(editorMapDivRef.current, {
          center: form.polygon?.length ? { lat: form.polygon[0][0], lng: form.polygon[0][1] } : DEFAULT_CENTER,
          zoom: 12,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        });
      } else {
        // Trigger a resize because the div was hidden when the map was created
        g.maps.event.trigger(editorMapRef.current, "resize");
      }

      // Cleanup any prior polygon / drawing state
      if (editorPolygonRef.current) {
        editorPolygonRef.current.setMap(null);
        editorPolygonRef.current = null;
      }
      cleanupDrawingState();

      // If editing an existing zone, render the polygon as editable
      if (form.polygon && form.polygon.length >= 3) {
        const path = form.polygon.map(([lat, lng]) => ({ lat, lng }));
        const poly = new g.maps.Polygon({
          paths: path,
          strokeColor: form.color,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: form.color,
          fillOpacity: 0.2,
          editable: true,
          draggable: true,
          map: editorMapRef.current,
        });
        editorPolygonRef.current = poly;
        attachPolygonChangeListeners(poly);
        // Fit
        const b = new g.maps.LatLngBounds();
        path.forEach((pt) => b.extend(pt));
        editorMapRef.current.fitBounds(b, 40);
      } else {
        // Manual click-to-draw mode for new zone
        startDrawingMode();
      }
    }, 80);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const attachPolygonChangeListeners = (poly) => {
    const path = poly.getPath();
    const update = () => syncFormFromPolygon(poly);
    googleRef.current.maps.event.addListener(path, "set_at", update);
    googleRef.current.maps.event.addListener(path, "insert_at", update);
    googleRef.current.maps.event.addListener(path, "remove_at", update);
    googleRef.current.maps.event.addListener(poly, "dragend", update);
  };

  const syncFormFromPolygon = (poly) => {
    const path = poly.getPath();
    const pts = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      pts.push([p.lat(), p.lng()]);
    }
    setForm((f) => ({ ...f, polygon: pts }));
  };

  // Color change should reflect in the active polygon (and in-progress drawing artefacts)
  useEffect(() => {
    if (editorPolygonRef.current) {
      editorPolygonRef.current.setOptions({
        strokeColor: form.color,
        fillColor: form.color,
      });
    }
    const state = drawingStateRef.current;
    if (state.polyline) {
      state.polyline.setOptions({ strokeColor: form.color });
    }
    state.markers.forEach((m, idx) => {
      if (idx === 0) return; // keep first vertex green for visibility
      const icon = m.getIcon();
      m.setIcon({ ...icon, fillColor: form.color });
    });
  }, [form.color]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, color: PALETTE[routes.length % PALETTE.length] });
    setOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      id: r.id,
      name: r.name,
      color: r.color || PALETTE[0],
      polygon: r.polygon || [],
      assigned_driver_ids: r.assigned_driver_ids || [],
      assigned_branch_id: r.assigned_branch_id || null,
      notes: r.notes || "",
      is_active: r.is_active,
    });
    setOpen(true);
  };

  const closeEditor = () => {
    setOpen(false);
    setForm(EMPTY_FORM);
    if (editorPolygonRef.current) {
      editorPolygonRef.current.setMap(null);
      editorPolygonRef.current = null;
    }
    cleanupDrawingState();
  };

  const clearDrawnPolygon = () => {
    if (editorPolygonRef.current) {
      editorPolygonRef.current.setMap(null);
      editorPolygonRef.current = null;
    }
    cleanupDrawingState();
    setForm((f) => ({ ...f, polygon: [] }));
    // Re-enter drawing mode
    startDrawingMode();
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Route name is required");
      return;
    }
    if (!form.polygon || form.polygon.length < 3) {
      toast.error("Draw a polygon with at least 3 vertices");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color,
        polygon: form.polygon,
        assigned_driver_ids: form.assigned_driver_ids,
        assigned_branch_id: form.assigned_branch_id || null,
        notes: form.notes || null,
        is_active: !!form.is_active,
      };
      if (form.id) {
        await api.patch(`/routes/${form.id}`, payload);
        toast.success("Route updated");
      } else {
        await api.post("/routes", payload);
        toast.success("Route created");
      }
      closeEditor();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete route "${r.name}"? This won't unassign existing packages.`)) return;
    try {
      await api.delete(`/routes/${r.id}`);
      toast.success("Route deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleDriver = (did) => {
    setForm((f) => {
      const has = f.assigned_driver_ids.includes(did);
      return {
        ...f,
        assigned_driver_ids: has
          ? f.assigned_driver_ids.filter((x) => x !== did)
          : [...f.assigned_driver_ids, did],
      };
    });
  };

  const driverName = (id) => drivers.find((d) => d.id === id)?.name || id;
  const branchName = (id) => branches.find((b) => b.id === id)?.name;

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="routes-page">
      <Toaster position="top-right" />

      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fleet Operations</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
            Delivery Routes
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Draw delivery zones on the map. Assign drivers + branches. Packages auto-tag to a zone based on recipient coordinates.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={openCreate}
            className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
            data-testid="new-route-btn"
          >
            <Plus size={15} className="mr-1.5" weight="bold" /> New route
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Map */}
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <div
            ref={mapDivRef}
            className="w-full h-[560px]"
            data-testid="routes-overview-map"
          />
        </div>

        {/* Sidebar list */}
        <div className="bg-white border border-zinc-200 rounded-md p-4 max-h-[560px] overflow-y-auto" data-testid="routes-list">
          {loading ? (
            <div className="text-sm text-zinc-500">Loading routes…</div>
          ) : routes.length === 0 ? (
            <div className="text-center py-10">
              <Polygon size={28} className="mx-auto text-zinc-300 mb-2" />
              <div className="text-sm text-zinc-500">No routes yet</div>
              {canManage && (
                <Button size="sm" variant="outline" className="mt-3" onClick={openCreate} data-testid="empty-new-route-btn">
                  Draw your first zone
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {routes.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-md border p-3 cursor-pointer transition-colors ${selectedId === r.id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}
                  onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                  data-testid={`route-row-${r.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="w-3 h-3 rounded-sm mt-1 shrink-0"
                      style={{ backgroundColor: r.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-zinc-900 truncate text-sm">{r.name}</div>
                        {!r.is_active && (
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <PackageIcon size={10} weight="bold" />
                          {r.package_count} pkg
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Truck size={10} weight="bold" />
                          {(r.assigned_driver_ids || []).length} driver{(r.assigned_driver_ids || []).length === 1 ? "" : "s"}
                        </span>
                        {r.assigned_branch_id && branchName(r.assigned_branch_id) && (
                          <span className="inline-flex items-center gap-1">
                            <Buildings size={10} weight="bold" />
                            {branchName(r.assigned_branch_id)}
                          </span>
                        )}
                      </div>
                      {selectedId === r.id && (
                        <div className="mt-2 text-[11px] text-zinc-600 space-y-1">
                          {(r.assigned_driver_ids || []).length > 0 && (
                            <div>
                              <b>Drivers:</b> {r.assigned_driver_ids.map(driverName).join(", ")}
                            </div>
                          )}
                          {r.notes && <div className="text-zinc-500 italic">{r.notes}</div>}
                          {canManage && (
                            <div className="flex gap-1 pt-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                                data-testid={`edit-route-${r.id}`}
                              >
                                <PencilSimple size={11} className="mr-1" /> Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-red-600 hover:text-red-700"
                                onClick={(e) => { e.stopPropagation(); remove(r); }}
                                data-testid={`delete-route-${r.id}`}
                              >
                                <Trash size={11} className="mr-1" /> Delete
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) closeEditor(); }}>
        <DialogContent
          className="w-[calc(100vw-1.5rem)] sm:w-[95vw] max-w-[1100px] max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden p-0 gap-0"
          data-testid="route-editor-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-200 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <MapTrifold size={18} className="text-[#002BF6]" weight="bold" />
              {form.id ? "Edit route" : "Draw a new route zone"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_320px] overflow-hidden min-h-0">
            {/* Map column */}
            <div className="bg-zinc-100 relative">
              <div
                ref={editorMapDivRef}
                className="w-full h-full min-h-[360px]"
                data-testid="route-editor-map"
              />
              {(!form.polygon || form.polygon.length < 3) && (
                <div className="absolute bottom-3 left-3 right-3 bg-white border border-zinc-200 rounded-md p-2 text-xs text-zinc-700 shadow-sm" data-testid="route-draw-hint">
                  <b>Click on the map</b> to drop polygon vertices. Once you have 3+ vertices, click the green starting dot to close the shape.
                  {form.polygon?.length > 0 && (
                    <span className="ml-1 text-zinc-500">· {form.polygon.length} vertex{form.polygon.length === 1 ? "" : "es"} placed</span>
                  )}
                </div>
              )}
              {form.polygon && form.polygon.length >= 3 && (
                <div className="absolute top-3 right-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearDrawnPolygon}
                    className="bg-white"
                    data-testid="clear-polygon-btn"
                  >
                    <X size={12} className="mr-1" /> Clear & redraw
                  </Button>
                </div>
              )}
            </div>

            {/* Form column */}
            <div className="border-t md:border-t-0 md:border-l border-zinc-200 overflow-y-auto p-4 space-y-3.5 bg-white">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Zone name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5"
                  placeholder="e.g. Kingston Central"
                  data-testid="route-name-input"
                />
              </div>

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Display color</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-7 h-7 rounded-md transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-zinc-900 scale-110" : ""}`}
                      style={{ backgroundColor: c }}
                      data-testid={`route-color-${c.slice(1)}`}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Assigned drivers</Label>
                <div className="mt-1.5 max-h-32 overflow-y-auto border border-zinc-200 rounded-md p-2 space-y-1" data-testid="route-drivers-list">
                  {drivers.length === 0 ? (
                    <div className="text-xs text-zinc-500 py-2 text-center">No drivers in your network yet</div>
                  ) : (
                    drivers.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-50 px-1 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={form.assigned_driver_ids.includes(d.id)}
                          onChange={() => toggleDriver(d.id)}
                          className="rounded"
                          data-testid={`route-driver-toggle-${d.id}`}
                        />
                        <span className="flex-1 truncate">{d.name}</span>
                        <span className="text-[10px] text-zinc-400 uppercase">{d.vehicle_type}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {branches.length > 0 && (
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Origin branch (optional)</Label>
                  <select
                    value={form.assigned_branch_id || ""}
                    onChange={(e) => setForm({ ...form, assigned_branch_id: e.target.value || null })}
                    className="mt-1.5 w-full h-9 rounded-md border border-zinc-300 px-2 text-sm bg-white"
                    data-testid="route-branch-select"
                  >
                    <option value="">— None —</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Internal notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="mt-1.5 text-sm"
                  placeholder="e.g. Morning route, avoid Hope Rd 5–7pm"
                  data-testid="route-notes-input"
                />
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="route-active-toggle"
                />
                <span>Active — match packages to this zone</span>
              </label>

              <div className="rounded-md bg-zinc-50 border border-zinc-200 p-2 text-[11px] text-zinc-600">
                Vertices: <b className="font-mono">{form.polygon?.length || 0}</b>
                {form.polygon?.length >= 3 && " · ready to save"}
              </div>
            </div>
          </div>

          <DialogFooter className="px-5 py-3 border-t border-zinc-200 bg-white shrink-0">
            <Button variant="outline" onClick={closeEditor} data-testid="route-cancel-btn">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || !form.name.trim() || (form.polygon?.length || 0) < 3}
              className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
              data-testid="save-route-btn"
            >
              {saving ? "Saving…" : form.id ? "Save changes" : "Create route"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
